import * as ents from '@attain-apps/entities'
import type * as router from '@attain-apps/fe/web/main/lib/router'
import type * as store from '@attain-apps/fe/web/main/lib/store'
import { TypeChecker } from '@attain-apps/type-guards'

import type * as dms from '../dms'

import type { Feature } from './feature'
import * as models from './models'

export class Selectors {
  /**
   * Focused variables Holder
   */
  public readonly focused

  /**
   * Returns true if currently focused holder is widget
   */
  public readonly isWidgetFocused

  /**
   * Returns true if currently focused holder is session
   */
  public readonly isSessionFocused

  /**
   * Returns true if currently focused holder is page
   */
  public readonly isPageFocused

  /**
   * List of variable definitions of focused variables holder
   */
  public readonly focusedDefinitions

  /**
   * Values for focused holder from both active and editing states
   */
  public readonly focusedValues

  /**
   * Returns true if there are variable definitions for a given holder
   */
  public readonly hasDefinitions

  /**
   * Gets variable values following the inheritance chain:
   *
   * `link` → `session` → `scenario` → `page` → `widget`
   *
   * Child values override parent values in the chain.
   *
   * @see {@link _getVariableValues}
   */
  // TODO: scenario variables are not implemented
  public readonly getVariableOverrides

  /**
   * Returns true if the variable values for the focused holder have been
   * edited. Typically used to check whether the values differ from their
   * default state.
   */
  public readonly isFocusedValuesModified

  /**
   * Get variable value for a given variable id from currently focused variables
   * holder
   */
  public readonly getFromFocusedValues

  /**
   * Returns true if there are applied values for a given holder
   */
  public readonly hasAppliedValues

  constructor(
    feature: Feature,
    router: router.Router,
    schemaFeature: dms.schema.Feature,
  ) {
    const _definitionsLookup = feature.state.project(
      state => state.definitionsLookup,
    )
    const _castedDefinitionsLookup = _definitionsLookup.project(
      schemaFeature.selectors.schema.asSelector,
      (definitionsLookup, schema) =>
        Object.fromEntries(
          Object.entries(definitionsLookup).map(([holder, definitions]) => [
            holder,
            definitions?.map(definition =>
              // By default, definition default value is just parsed json received
              // from API (typed as unknown value). We need to cast each
              // definition value based on the type
              models.Definition.castValue(definition, schema),
            ),
          ]),
        ),
    )
    const _valuesLookup = feature.state.project(state => state.valuesLookup)
    const _editingValuesLookup = feature.state.project(
      state => state.editingValuesLookup,
    )
    const _currentLinkVariableValues = router.selectors.getEncodedQueryParam(
      models.LinkValues.SCOPE,
    ) satisfies store.selectors.BoundSelector<unknown> as store.selectors.BoundSelector<
      models.LinkValues | undefined
    >
    this.focused = feature.state.project(state => state.focused)
    this.isWidgetFocused = this.focused.project(focused =>
      TypeChecker.isNotNullish(focused?.widget),
    )
    this.isPageFocused = this.focused.project(focused =>
      TypeChecker.isNotNullish(focused?.page),
    )
    this.isSessionFocused = this.focused.project(focused =>
      TypeChecker.isNotNullish(focused?.session),
    )
    this.focusedDefinitions = this.focused.project(
      _castedDefinitionsLookup.asSelector,
      (focused, definitions) =>
        focused
          ? (definitions[models.Definition.Holder.stringify(focused)] ?? [])
          : [],
    )
    const _focusedAppliedValues = _valuesLookup.project(
      this.focused.asSelector,
      (valuesLookup, focused) =>
        focused
          ? (valuesLookup[models.Values.Holder.stringify(focused)] ?? {})
          : {},
    )
    const _focusedEditingValues = _editingValuesLookup.project(
      this.focused.asSelector,
      (editingValuesLookup, focused) =>
        focused
          ? (editingValuesLookup[models.Values.Holder.stringify(focused)] ?? {})
          : {},
    )
    this.focusedValues = this.focusedDefinitions.project(
      _focusedAppliedValues.asSelector,
      _focusedEditingValues.asSelector,
      (definitions, focusedAppliedValues, focusedEditingValues) => {
        // Start with default values from definitions
        const defaultValues =
          ents.core.dms.data.query.Variable.Lookup.usingDefaults(definitions)

        return {
          ...defaultValues,
          ...focusedAppliedValues,
          ...focusedEditingValues,
        }
      },
    )
    this.getFromFocusedValues = feature.createSelectorMethod(
      ({ id }: { id: ents.core.dms.data.query.Variable.Name }) =>
        this.focusedValues.project(focusedValues => focusedValues?.[id])
          .asSelector,
    )

    this.hasDefinitions = feature.createSelectorMethod(
      (holder: models.Definition.Holder) =>
        _castedDefinitionsLookup.project(
          definitions =>
            (definitions[models.Definition.Holder.stringify(holder)] ?? [])
              .length > 0,
        ),
    )

    this.hasAppliedValues = feature.createSelectorMethod(
      (holder: models.Values.Holder) =>
        _valuesLookup.project(
          values =>
            Object.values(
              values[models.Values.Holder.stringify(holder)] ?? [],
            ).filter(TypeChecker.isNotNullish.raw).length > 0,
        ),
    )

    this.isFocusedValuesModified = _valuesLookup.project(
      this.focused.asSelector,
      (valuesLookup, focused) => {
        // If no focused holder, return empty object
        if (!focused) {
          return false
        }

        const holderKey = models.Values.Holder.stringify(focused)

        // Check if there are any edited values for the focused holder
        return Object.values(valuesLookup[holderKey] ?? {}).some(
          TypeChecker.isNotNullish.raw,
        )
      },
    )

    this.getVariableOverrides = feature.createSelectorMethod(
      (holder?: models.Values.Holder) =>
        _valuesLookup.project(
          _currentLinkVariableValues.asSelector,
          (valuesLookup, linkVariableValues = {}) =>
            _getVariableValues({
              holder,
              linkVariableValues,
              valuesLookup,
            }),
        ),
    )
  }
}

/**
 * Get variable values based on current hierarchy for a given holder.
 * Values are collected in this order: link variables, session variables,
 * scenario variables, page variables, and widget variables, with each level
 * inheriting from its parents
 */
function _getVariableValues({
  holder,
  valuesLookup,
  linkVariableValues,
}: {
  holder?: models.Values.Holder
  valuesLookup: models.Values.Lookup
  linkVariableValues: models.LinkValues
}): models.Values {
  /**
   * Get all variable values based on current hierarchy for a given holder.
   * Values always inherit from their parents.
   */
  function _getCompleteVariableValues({
    parent,
  }: {
    readonly parent?: models.Values.Holder
  } = {}) {
    if (!holder) {
      return linkVariableValues
    }

    const values = valuesLookup[models.Values.Holder.stringify(holder)]

    return {
      ...linkVariableValues,
      ...(parent
        ? _getVariableValues({
            holder: parent,
            linkVariableValues,
            valuesLookup,
          })
        : undefined),
      ...values,
    }
  }

  const { page, widget, session } = holder ?? {}

  if (session) {
    return _getCompleteVariableValues()
  }
  if (page) {
    return _getCompleteVariableValues({ parent: { session: page } })
  }
  if (widget) {
    return _getCompleteVariableValues({ parent: { page: widget } })
  }

  return linkVariableValues
}
